import {
  IAssignSubmissionCommand,
  ICloneSubmissionCommand,
  ICreateSubmissionCommand,
  IEditSubmissionCommand,
  IFormActionSubtype,
  IRequestReviewCommand,
  IEnqueueReviewRequestCommand,
  ISubmissionAssignmentSubtype,
  ISubmissionNoteCommand,
  IExportSubmissionCommand,
  ISubmitFirstAssessmentCommand,
  IUnAssignSubmissionCommand,
  IRequestExpertsReviewCommand,
  IAuthorCompleteChangeRequestCommand,
  IEditorialSubmitChangeRequestCommand,
  IChangeRequestPayload,
  ISubmitEarlyAssessmentReviewCommand,
  ISubmitAdjudicationReviewCommand,
  IDeleteNoteCommand,
  IUpdateNoteCommand,
  IUpdateNotePayload,
  ICreateNotePayload,
  IDeleteNotePayload,
  IAssignCOCEditorCommand,
  ISubmitCOCRecommendationCommand,
  ICOCAssignSubmissionCommand,
  IUnassignCOCRoleCommand,
  IModerateCOCRecommendationCommand
} from '@publishing-engine/domain/actions/v1';
import {
  IOrganizationInteractionCommand,
  ISubmissionDomainCommand,
  ISubmissionInteractionCommand
} from '@publishing-engine/domain/actions/v1/commands';
import {
  IEarlyAssessmentEditorialRequest,
  IChangeArticleType,
  IEditSubmission,
  IFirstAssessmentRescission,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IReviewerInvitation,
  IReviewerInvitationQueue,
  IReviewScore,
  IRoleType,
  ISubmissionFile,
  IWorkPhase
} from '@publishing-engine/domain/models';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import axiosInstance from './client';
import { IEditorialSubmissionDecisionCommand } from '../../../domain/actions/v1/editorialSubmissionDecision';
import {
  ISubmissionNote,
  ISubmissionNotesManagementListView
} from '@publishing-engine/domain/views';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { FormFieldSpecification } from '@publishing-engine/domain/types/article';
import { IEditorialFormDataCommand } from '@publishing-engine/domain/actions/v1/createEditorialFormData';
import {
  FETCH_ACTIVITY_LOG_KEY,
  MY_SUBMISSIONS_TOTALS_QUERY_KEY,
  USER_ACTIVITY_TOTALS_QUERY_KEY
} from 'src/constants';
import { queryClient as appQueryClient } from 'src/utils/queryClient';
import {
  IAdjudicationRequestDecisionCommand,
  IEarlyAssessmentRequestDecisionCommand,
  IPeerReviewDecisionCommand
} from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/peerReviewDecision';
import {
  IModerateReviewSubTypes,
  IPeerReviewModerateCommand
} from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/peerReviewModerate';
import {
  CoReviewDeclineRoute,
  CoReviewInvitationRoute,
  ICommandHandlerResponse,
  OrganizationCommandInteractionRoute,
  ReviewDeclineInvitationRoute,
  ReviewDeclineRoute
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { IRateReviewCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/rateReview';
import { ISubmitRevisionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/submitRevision';
import { IRescindFirstAssessmentCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/rescindReview';
import {
  IExtendReviewDeadlineCommand,
  IExtendSubmissionDeadlineCommand,
  IRequestReviewDeadlineExtensionCommand,
  IRequestSubmissionDeadlineExtensionCommand
} from '@publishing-engine/domain/actions/v1/extendDeadline';
import { ISubmitLicensingCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/submitLicensing';
import { IRemoveEnqueuedReviewerCommand } from '@publishing-engine/domain/actions/v1/removeEnqueuedReviewer';
import { IRequestRevisionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/requestRevision';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IRemoveReviewCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/removeReview';
import { IRequestRevisionReviewCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/requestRevisionReview';
import { ISetMinReviewCountCommand } from '@publishing-engine/domain/actions/v1/setMinReviewCount';
import { ISubmitRevisionAssessmentCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/submitRevisionAssessment';
import { IToggleQueueCommand } from '@publishing-engine/domain/actions/v1/toggleQueuePause';
import { IRequestReviewInRevisionCommand } from '@publishing-engine/domain/actions/v1/reviewInRevisionRequested';
import { IEditorialOfferTransferCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialOfferTransfer';
import { IDeclineTransferCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/declineTransfer';
import { IAcceptTransferOfferCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/acceptTransfer';
import { IApproveSubmissionForPreProductionCommand } from '@publishing-engine/domain/actions/v1/approveSubmissionForPreProduction';
import { IEditorialRejectSubmissionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialRejectSubmission';
import {
  IEditorialAcceptSubmissionWithdrawalCommand,
  IEditorialWithdrawSubmissionCommand
} from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialWithdrawSubmission';
import {
  IAuthorRequestSubmissionWithdrawalCommand,
  IAuthorRevokeSubmissionWithdrawalRequestCommand
} from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorWithdrawSubmission';
import { IEditorialUnsubmitSubmissionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialUnsubmitSubmission';
import { IAuthorResubmitSubmissionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorResubmitSubmission';
import { IAuthorEditSubmissionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorEditSubmission';
import { IAcceptForFormalAcceptanceCommand } from '@publishing-engine/domain/actions/v1/acceptForFormalAcceptance';
import { IEditorialRequestImmediateRevisionCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialRequestImmediateRevision';
import { IAuthorSubmitCopyrightCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorSubmitCopyright';
import { IEditorialUploadCopyrightCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialUploadCopyright';
import { IEditorialRequestCopyrightCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialRequestCopyright';
import { IPeerReviewWithCaseData } from '@publishing-engine/domain/models/review';
import { IResendDeclinedReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/resendDeclinedReviewInvitation';
import { IResendExpiredReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/resendExpiredReviewInvitation';
import { IExpertFirstAssessment } from 'src/pages/MyTask/Adjudications/adjudicationType';
import { IEditorialWithdrawInvitedReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/editorialWithdrawInvitedReviewInvitation';
import { IEditorialWithdrawAcceptedReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/editorialWithdrawAcceptedReviewInvitation';
import { IResendWithdrawnReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/resendWithdrawnReviewInvitation';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import { ISendAdhocEmailCommand } from '@publishing-engine/domain/actions/v1/adhoc-email';
import {
  IDenyReviewDeadlineExtensionCommand,
  IDenySubmissionDeadlineExtensionCommand
} from '@publishing-engine/domain/actions/v1/denyDeadlineExtension';
import { IEditorialWithdrawAwaitingRevisionReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/editorialWithdrawAwaitingRevisionReviewInvitation';
import { IEditorialWithdrawRevisionInvitedReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/editorialWithdrawRevisionInvitedReviewInvitation';
import { IEditorialWithdrawRevisionAssessmentReviewInvitationCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/editorialWithdrawRevisionAssessmentReviewInvitation';
import { IChangeArticleTypeCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/changeArticleType';
import {
  IRevertDecisionCommand,
  IRevertDecisionCommandData
} from '@publishing-engine/domain/actions/v1/submissionWorkflow/revertDecision';
import { IAuthorUploadCopyrightCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorUploadCopyright';
import { IResetAuthorCopyrightCommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/resetCopyrightSubmission';
import { IAuthorUploadICMJECommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/authorUploadICMJE';
import { IEditorialUploadICMJECommand } from '@publishing-engine/domain/actions/v1/submissionWorkflow/editorialUploadICMJE';
import { IRecommendationForm } from 'src/pages/SubmissionDetails/RecommendationForm';
import { USER_DATA_LOCAL_STORAGE_KEY } from '../hooks/useUserDeviceData';
import { getLocalStorageItem } from '../hooks/useLocalStorage';
import { IUserDeviceData } from '@publishing-engine/domain/types';
import axios from 'axios';

type OmitUserIdProperty<T> = Omit<T, 'userId'>;
type ISubmissionManagementPayload = {
  role: string;
} & (
  | {
      action: 'assign';
      assigneeId: string;
    }
  | {
      action: 'unassign';
    }
);

export function sendCommandWithOTT({
  type,
  subtype,
  userId,
  reviewId,
  submissionId,
  submissionContextId,
  organizationId,
  token
}: {
  type: string;
  subtype: string;
  userId: string;
  reviewId: string;
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
  token: string;
}) {
  const udd: IUserDeviceData = getLocalStorageItem(
    USER_DATA_LOCAL_STORAGE_KEY,
    {}
  );

  // must not be empty object
  const userDeviceData = Object.keys(udd).length === 0 ? undefined : udd;
  return axios.post<unknown>(
    `/api/submission-contexts/${submissionContextId}/commands`,
    {
      command: {
        type,
        subtype,
        userId,
        reviewRequestId: reviewId,
        submissionId,
        submissionContextId,
        organizationId
      },
      userDeviceData
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
}

const commandHandler = <T>(
  command: OmitUserIdProperty<ISubmissionDomainCommand>
) => {
  const { submissionContextId } = command;
  const userDeviceData: IUserDeviceData = getLocalStorageItem(
    USER_DATA_LOCAL_STORAGE_KEY,
    {}
  );

  return axiosInstance
    .post<T>(`submission-contexts/${submissionContextId}/commands`, {
      command,
      userDeviceData
    })
    .then(r => ({
      ...r.data,
      isSuccess: true as const
    }))
    .catch(e => ({
      message: e.response.data,
      isSuccess: false as const
    }));
};

export type ICommandResponse =
  | (ICommandHandlerResponse & { isSuccess: true })
  | {
      message: any;
      isSuccess: false;
    };

// The My Submissions tab counts are their own query, so nothing else refreshes
// them - a submission moving from one tab to another (e.g. a revision being
// submitted) would leave the counts stale until the page remounts. The
// navigation reads its own activity totals from a separate query for the same
// reason.
function getSubmissionSuccessCallback(invalidateSubmissionQuery: () => void) {
  return function (data: ICommandResponse) {
    if (data.isSuccess) {
      invalidateSubmissionQuery();
      appQueryClient.invalidateQueries({
        queryKey: [MY_SUBMISSIONS_TOTALS_QUERY_KEY]
      });
      appQueryClient.invalidateQueries({
        queryKey: [USER_ACTIVITY_TOTALS_QUERY_KEY]
      });
      // We used to use the POST /command response in this callback to refresh local submission state.
      // With the Q4 2024 work on generalizing Submission Field behaviour, the response
      // formats have diverged. We should consider aligning the POST /commands response
      // with the new required format to prevent data overfetching.
      // More info - https://www.notion.so/morressier/Decoupling-Dynamic-Submission-Fields-from-AuthorSubmission-form-157f7da2eaa98054a4eeda2cded18763
    }
  };
}

const interactionCommandHandler = <T>(
  command: OmitUserIdProperty<ISubmissionInteractionCommand>
) => {
  const { submissionContextId } = command;
  return axiosInstance
    .post<T>(`submission-contexts/${submissionContextId}/commands/interact`, {
      command
    })
    .then(r => ({
      ...r.data,
      isSuccess: true as const
    }))
    .catch(e => ({
      message: e.response.data,
      isSuccess: false as const
    }));
};

const organizationInteractionHandler = <T>(
  command: OmitUserIdProperty<IOrganizationInteractionCommand>
) => {
  return axiosInstance
    .post<T>(OrganizationCommandInteractionRoute.build({}), { command })
    .then(r => ({
      ...r.data,
      isSuccess: true as const
    }))
    .catch(e => ({
      message: e.response.data,
      isSuccess: false as const
    }));
};

export const useUpdateEditorialFormData = (
  submissionId: string,
  submissionContextId: string,
  articleWorkflowId: string,
  phase: IWorkPhase,
  queryKey: QueryKey,
  organizationId: string
) => {
  const queryClient = useQueryClient();
  const updateEditorialFormDataMutation = useMutation({
    mutationFn: (caseData: Record<string, unknown>) => {
      const command: OmitUserIdProperty<IEditorialFormDataCommand> = {
        type: 'CreateEditorialFormData',
        submissionId,
        submissionContextId,
        organizationId,
        articleWorkflowId,
        phase,
        caseData
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: (_data, caseData) => {
      queryClient.invalidateQueries({ queryKey: [FETCH_ACTIVITY_LOG_KEY] });

      return queryClient.setQueryData(queryKey, (state: any) => {
        return {
          ...state,
          editorialFormData: {
            ...state?.editorialFormData,
            caseData: {
              ...(state?.editorialFormData?.caseData || {}),
              ...caseData
            }
          }
        };
      });
    }
  });

  return updateEditorialFormDataMutation;
};

export const useAcceptForPeerReview = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload
) => {
  const acceptForPeerReviewMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IEditorialSubmissionDecisionCommand> = {
        type: 'EditorialSubmissionDecision',
        subtype: 'approveForPeerReview',
        submissionContextId,
        organizationId,
        submissionId,
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return acceptForPeerReviewMutation;
};

export const useAcceptSubmission = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const acceptSubmissionMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IEditorialSubmissionDecisionCommand> = {
        type: 'EditorialSubmissionDecision',
        subtype: 'accept',
        submissionContextId,
        organizationId,
        submissionId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });
  return acceptSubmissionMutation;
};

export const useRejectSubmission = (
  submissionId: string,
  submissionContextId: string,
  articleWorkflowId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload,
  notifyReviewerIds?: string[]
) => {
  const rejectSubmissionMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialRejectSubmissionCommand> = {
        type: 'EditorialRejectSubmission',
        formType: 'editorialRejectSubmission',
        submissionId,
        submissionContextId,
        organizationId,
        articleWorkflowId,
        emails,
        notifyReviewerIds,
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },

    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return rejectSubmissionMutation;
};

export const useProvisionalAcceptance = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload,
  notifyReviewerIds?: string[]
) => {
  const provisionalAcceptanceMutation = useMutation({
    mutationFn: (editorComment: string) => {
      const command: OmitUserIdProperty<IApproveSubmissionForPreProductionCommand> =
        {
          type: 'ApproveSubmissionForPreProduction',
          submissionContextId,
          organizationId,
          submissionId,
          editorComment,
          emails,
          notifyReviewerIds
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return provisionalAcceptanceMutation;
};

export const useFormalAcceptance = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload,
  notifyReviewerIds?: string[]
) => {
  const formalAcceptanceMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IAcceptForFormalAcceptanceCommand> = {
        type: 'AcceptForFormalAcceptance',
        submissionContextId,
        organizationId,
        submissionId,
        emails,
        notifyReviewerIds
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return formalAcceptanceMutation;
};

export const useCloneSubmission = (
  sourceSubmissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const cloneSubmissionMutation = useMutation({
    mutationFn: () => {
      const command: Pick<
        OmitUserIdProperty<ICloneSubmissionCommand>,
        'type' | 'sourceSubmissionId' | 'submissionContextId' | 'organizationId'
      > = {
        type: 'CloneSubmission',
        sourceSubmissionId,
        submissionContextId,
        organizationId
      };

      return commandHandler<ICommandHandlerResponse>(
        command as OmitUserIdProperty<ISubmissionDomainCommand>
      );
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return cloneSubmissionMutation;
};

export const useOverwriteMinReviewers = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const queryClient = useQueryClient();
  const setMinimumReviewCountMutation = useMutation({
    mutationFn: (count: number) => {
      const command: OmitUserIdProperty<ISetMinReviewCountCommand> = {
        type: 'SetMinReviewCount',
        submissionContextId,
        count,
        submissionId,
        organizationId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },

    onSuccess: () => {
      invalidateSubmissionQueries();
      queryClient.invalidateQueries({
        queryKey: ['overview-details']
      });
    }
  });

  return setMinimumReviewCountMutation;
};

export const useInviteReviewer = (
  submissionId: string,
  submissionContextId: string,
  queryKey: QueryKey,
  organizationId: string,
  subType?: string
) => {
  const queryClient = useQueryClient();

  const inviteReviewerMutation = useMutation({
    mutationFn: (payload: {
      reviewer: { id: string };
      emails?: IEmailCommandPayload;
      reviewRequestId?: string;
      editorialRequest?: {
        comment?: string;
        attachments?: ISubmissionFile[];
      };
    }) => {
      const { reviewer, emails, reviewRequestId, editorialRequest } = payload;
      let finalEmails = emails;
      if (emails && emails.length > 0 && editorialRequest?.attachments) {
        const firstEmail = emails[0]!;
        finalEmails = [
          {
            body: firstEmail.body,
            recipients: firstEmail.recipients,
            subject: firstEmail.subject,
            override: firstEmail.override,
            attachments: editorialRequest.attachments
          }
        ];
      }

      const command: OmitUserIdProperty<IRequestReviewCommand> = {
        type: 'RequestReview',
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        reviewerId: reviewer.id,
        emails: finalEmails,
        ...(subType && { subtype: 'automaticBoardMember' }),
        ...(reviewRequestId && { reviewRequestId })
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: [FETCH_ACTIVITY_LOG_KEY] });
        queryClient.invalidateQueries([
          'submission-context',
          submissionContextId,
          submissionId,
          'reviews'
        ]);
        queryClient.invalidateQueries({
          queryKey: [
            'submission-context',
            submissionContextId,
            submissionId,
            'reviewer-invitations'
          ]
        });
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
        queryClient.invalidateQueries({
          queryKey
        });
      }
    }
  });

  return inviteReviewerMutation;
};

export const useInviteExperts = (
  submissionId: string,
  submissionContextId: string,
  queryKey: QueryKey,
  type: 'RequestEarlyAssessment' | 'RequestAdjudication',
  organizationId: string
) => {
  const queryClient = useQueryClient();

  const inviteReviewerMutation = useMutation({
    mutationFn: (reviewer: {
      id: string;
      editorialRequest: IEarlyAssessmentEditorialRequest;
      emails?: IEmailCommandPayload;
    }) => {
      const command: OmitUserIdProperty<IRequestExpertsReviewCommand> = {
        type,
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        expertId: reviewer.id,
        editorialRequest: reviewer.editorialRequest,
        emails: reviewer.emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return inviteReviewerMutation;
};

export const useRequestReviewInRevision = (
  submissionId: string,
  submissionContextId: string,
  queryKey: QueryKey,
  organizationId: string,
  subType?: string
) => {
  const queryClient = useQueryClient();

  const requestReviewInRevisionMutation = useMutation({
    mutationFn: (payload: {
      reviewer: { id: string };
      emails?: IEmailCommandPayload;
      reviewRequestId?: string;
    }) => {
      const { reviewer, emails, reviewRequestId } = payload;
      const command: OmitUserIdProperty<IRequestReviewInRevisionCommand> = {
        type: 'RequestReviewInRevision',
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        reviewerId: reviewer.id,
        emails,
        ...(subType && { subtype: 'automaticBoardMember' }),
        ...(reviewRequestId && { reviewRequestId })
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: [FETCH_ACTIVITY_LOG_KEY] });
        queryClient.invalidateQueries({
          queryKey: [
            'submission-context',
            submissionContextId,
            submissionId,
            'reviews'
          ]
        });
        queryClient.invalidateQueries({
          queryKey: [
            'submission-context',
            submissionContextId,
            submissionId,
            'reviewer-invitations'
          ]
        });
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
        queryClient.invalidateQueries({
          queryKey: ['submission-context', submissionContextId]
        });
        return queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return requestReviewInRevisionMutation;
};

export const useAcceptReviewRequest = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  queryKey
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  const acceptReviewRequestMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IPeerReviewDecisionCommand> = {
        type: 'PeerReviewDecision',
        subtype: 'acceptRequest',
        submissionId: submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        reviewRequestId: reviewId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });
        queryClient.invalidateQueries({ queryKey: ['review', { reviewId }] });
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return acceptReviewRequestMutation;
};

export const useReviewRequestQueue = (
  submissionId: string,
  submissionContextId: string,
  enqueuedReviewersKey: QueryKey,
  organizationId: string,
  autoInvite?: boolean
) => {
  const queryClient = useQueryClient();

  const inviteReviewersMutation = useMutation({
    mutationFn: (reviewers: IReviewerInvitationQueue[]) => {
      const command: OmitUserIdProperty<IEnqueueReviewRequestCommand> = {
        type: 'EnqueueReviewRequest',
        submissionId,
        organizationId,
        submissionContextId,
        queue: reviewers,
        autoInvite: autoInvite
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: enqueuedReviewersKey });
      }
    }
  });

  return inviteReviewersMutation;
};

export const useRemoveEnqueuedReviewer = ({
  submissionId,
  submissionContextId,
  queryKey,
  organizationId
}: {
  submissionId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();

  const removeEnqueuedReviewerMutation = useMutation({
    mutationFn: ({
      reviewerInvitationId,
      reviewerEmail
    }: {
      reviewerInvitationId: string;
      reviewerEmail: string;
    }) => {
      const command: OmitUserIdProperty<IRemoveEnqueuedReviewerCommand> = {
        type: 'RemoveEnqueuedReviewer',
        submissionId,
        organizationId,
        submissionContextId,
        reviewQueueId: reviewerInvitationId,
        email: reviewerEmail
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return removeEnqueuedReviewerMutation;
};

export const useDeclineReviewRequest = ({
  submissionId,
  reviewId,
  submissionContextId,
  organizationId,
  queryKey
}: {
  submissionId: string;
  reviewId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();

  const declineReviewRequestMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IPeerReviewDecisionCommand> = {
        type: 'PeerReviewDecision',
        subtype: 'declineRequest',
        submissionId: submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        reviewRequestId: reviewId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });

        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return declineReviewRequestMutation;
};

export const useModeratePeerReview = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  reviewerId
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  reviewerId: string;
}) => {
  const queryClient = useQueryClient();

  const moderatePeerReviewMutation = useMutation({
    mutationFn: ({
      firstAssessment,
      subtype
    }: {
      firstAssessment: Pick<
        IPeerReviewFirstAssessment,
        'feedbackForAuthors' | 'reviewerAttachments'
      >;
      subtype: IModerateReviewSubTypes;
    }) => {
      const command: OmitUserIdProperty<IPeerReviewModerateCommand> = {
        type: 'PeerReviewModerate',
        subtype,
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId: reviewId,
        reviewerId,
        formPayload: firstAssessment,
        formType: 'peerReviewModerate'
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries([
          'submission-context',
          submissionContextId,
          submissionId,
          'reviews'
        ]);

        // Invalidate 'reviews-summary' key too
        queryClient.invalidateQueries(['reviews-summary', submissionId]);
      }
    }
  });

  return moderatePeerReviewMutation;
};

export const useRateReview = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  queryKey,
  reviewerId
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  reviewerId: string;
}) => {
  const queryClient = useQueryClient();
  const rateReviewMutation = useMutation({
    mutationFn: ({
      reviewScore,
      reviewRequestId,
      emails
    }: {
      reviewScore: IReviewScore;
      reviewRequestId?: string;
      emails?: IEmailCommandPayload;
    }) => {
      const command: OmitUserIdProperty<IRateReviewCommand> = {
        type: 'RateReview',
        submissionId,
        organizationId,
        submissionContextId,
        reviewerId,
        reviewRequestId: reviewRequestId || reviewId,
        formPayload: { reviewScore },
        formType: 'rateReview',
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
        queryClient.invalidateQueries({ queryKey: ['revision-history'] });
      }
    }
  });

  return rateReviewMutation;
};

export const useRescindFirstAssessment = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  queryKey,
  reviewerId
}: {
  submissionId: string;
  reviewId: string;
  submissionContextId: string;
  organizationId: string;
  queryKey: QueryKey;
  reviewerId: string;
}) => {
  const queryClient = useQueryClient();

  const rescindFirstAssessmentMutation = useMutation({
    mutationFn: ({
      rescindReview,
      reviewRequestId
    }: {
      rescindReview: IFirstAssessmentRescission;
      reviewRequestId?: string;
    }) => {
      const command: OmitUserIdProperty<IRescindFirstAssessmentCommand> = {
        type: 'RescindFirstAssessment',
        submissionId,
        organizationId,
        submissionContextId,
        reviewerId,
        reviewRequestId: reviewRequestId || reviewId,
        formType: 'rescindFirstAssessment',
        formPayload: rescindReview
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
      }
    }
  });

  return rescindFirstAssessmentMutation;
};

export const useResendDeclinedReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKey,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const resendDeclinedReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewerId,
      reviewRequestId,
      organizationId,
      emails
    }: {
      reviewerId: string;
      reviewRequestId: string;
      organizationId: string;
      emails?: IEmailCommandPayload;
    }) => {
      const command: OmitUserIdProperty<IResendDeclinedReviewInvitationCommand> =
        {
          type: 'ResendDeclinedReviewInvitation',
          reviewerId,
          reviewRequestId: reviewRequestId,
          organizationId,
          submissionId,
          submissionContextId,
          phase,
          emails
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
      }
    }
  });

  return resendDeclinedReviewInvitationMutation;
};

export const useResendExpiredReviewInvitation = ({
  organizationId,
  submissionId,
  submissionContextId,
  queryKey,
  phase
}: {
  organizationId: string;
  submissionId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const resendExpiredReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewerId,
      reviewRequestId,
      emails
    }: {
      reviewerId: string;
      reviewRequestId: string;
      emails?: IEmailCommandPayload;
    }) => {
      const command: OmitUserIdProperty<IResendExpiredReviewInvitationCommand> =
        {
          type: 'ResendExpiredReviewInvitation',
          reviewerId,
          reviewRequestId,
          organizationId,
          submissionId,
          submissionContextId,
          phase,
          emails
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
      }
    }
  });

  return resendExpiredReviewInvitationMutation;
};

export const useResendWithdrawnReviewInvitation = ({
  organizationId,
  submissionId,
  submissionContextId,
  queryKey,
  phase
}: {
  organizationId: string;
  submissionId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const resendWithdrawnReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewerId,
      reviewRequestId,
      emails,
      subtype
    }: {
      reviewerId: string;
      reviewRequestId: string;
      emails?: IEmailCommandPayload;
      subtype: string;
    }) => {
      const command: OmitUserIdProperty<IResendWithdrawnReviewInvitationCommand> =
        {
          type: 'ResendWithdrawnReviewInvitation',
          subtype,
          reviewerId,
          reviewRequestId: reviewRequestId,
          organizationId,
          submissionId,
          submissionContextId,
          phase,
          emails
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
      }
    }
  });

  return resendWithdrawnReviewInvitationMutation;
};

export const useEditorialWithdrawInvitedReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKeys,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKeys: QueryKey[];
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const editorialWithdrawInvitedReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewRequestId,
      reviewerId,
      organizationId
    }: {
      organizationId: string;
      reviewRequestId: string;
      reviewerId: string;
    }) => {
      const command: OmitUserIdProperty<IEditorialWithdrawInvitedReviewInvitationCommand> =
        {
          type: 'EditorialWithdrawInvitedReviewInvitation',
          reviewRequestId,
          reviewerId,
          organizationId,
          submissionId,
          submissionContextId,
          phase
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        for (const queryKey of queryKeys) {
          queryClient.invalidateQueries({ queryKey });
        }

        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
      }
    }
  });

  return editorialWithdrawInvitedReviewInvitationMutation;
};

export const useEditorialWithdrawAcceptedReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKeys,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKeys: QueryKey[];
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const editorialWithdrawAcceptedReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewRequestId,
      reviewerId,
      organizationId
    }: {
      reviewRequestId: string;
      reviewerId: string;
      organizationId: string;
    }) => {
      const command: OmitUserIdProperty<IEditorialWithdrawAcceptedReviewInvitationCommand> =
        {
          type: 'EditorialWithdrawAcceptedReviewInvitation',
          reviewRequestId,
          reviewerId,
          submissionId,
          organizationId,
          submissionContextId,
          phase
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        for (const queryKey of queryKeys) {
          queryClient.invalidateQueries({ queryKey });
        }
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
      }
    }
  });

  return editorialWithdrawAcceptedReviewInvitationMutation;
};

export const useEditorialWithdrawAwaitingRevisionReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKeys,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKeys: QueryKey[];
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const editorialWithdrawAwaitingRevisionReviewInvitationMutation = useMutation(
    {
      mutationFn: ({
        reviewRequestId,
        reviewerId,
        organizationId
      }: {
        reviewRequestId: string;
        reviewerId: string;
        organizationId: string;
      }) => {
        const command: OmitUserIdProperty<IEditorialWithdrawAwaitingRevisionReviewInvitationCommand> =
          {
            type: 'EditorialWithdrawAwaitingRevisionReviewInvitation',
            reviewRequestId,
            reviewerId,
            submissionId,
            organizationId,
            submissionContextId,
            phase
          };

        return commandHandler<ICommandHandlerResponse>(command);
      },
      onSuccess: data => {
        if (data.isSuccess) {
          for (const queryKey of queryKeys) {
            queryClient.invalidateQueries({ queryKey });
          }
          queryClient.invalidateQueries([
            'review-list',
            { submissionContextId, submissionId }
          ]);
        }
      }
    }
  );

  return editorialWithdrawAwaitingRevisionReviewInvitationMutation;
};

export const useEditorialWithdrawRevisionInvitedReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKeys,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKeys: QueryKey[];
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const editorialWithdrawRevisionInvitedReviewInvitationMutation = useMutation({
    mutationFn: ({
      reviewRequestId,
      reviewerId,
      organizationId
    }: {
      reviewRequestId: string;
      reviewerId: string;
      organizationId: string;
    }) => {
      const command: OmitUserIdProperty<IEditorialWithdrawRevisionInvitedReviewInvitationCommand> =
        {
          type: 'EditorialWithdrawRevisionInvitedReviewInvitation',
          reviewRequestId,
          reviewerId,
          submissionId,
          organizationId,
          submissionContextId,
          phase
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        for (const queryKey of queryKeys) {
          queryClient.invalidateQueries({ queryKey });
        }
        queryClient.invalidateQueries([
          'review-list',
          { submissionContextId, submissionId }
        ]);
      }
    }
  });

  return editorialWithdrawRevisionInvitedReviewInvitationMutation;
};

export const useEditorialWithdrawRevisionAssessmentReviewInvitation = ({
  submissionId,
  submissionContextId,
  queryKeys,
  phase
}: {
  submissionId: string;
  submissionContextId: string;
  queryKeys: QueryKey[];
  phase: IWorkPhase;
}) => {
  const queryClient = useQueryClient();

  const editorialWithdrawRevisionAssessmentReviewInvitationMutation =
    useMutation({
      mutationFn: ({
        reviewRequestId,
        reviewerId,
        organizationId
      }: {
        reviewRequestId: string;
        reviewerId: string;
        organizationId: string;
      }) => {
        const command: OmitUserIdProperty<IEditorialWithdrawRevisionAssessmentReviewInvitationCommand> =
          {
            type: 'EditorialWithdrawRevisionAssessmentReviewInvitation',
            reviewRequestId,
            reviewerId,
            submissionId,
            organizationId,
            submissionContextId,
            phase
          };

        return commandHandler<ICommandHandlerResponse>(command);
      },
      onSuccess: data => {
        if (data.isSuccess) {
          for (const queryKey of queryKeys) {
            queryClient.invalidateQueries({ queryKey });
          }
          queryClient.invalidateQueries([
            'review-list',
            { submissionContextId, submissionId }
          ]);
        }
      }
    });

  return editorialWithdrawRevisionAssessmentReviewInvitationMutation;
};

export const useSubmitFirstAssessmentReview = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateReviewContextData,
  firstName,
  lastName,
  email
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateReviewContextData: () => void;
  firstName: string;
  lastName: string;
  email: string;
}) => {
  const submitReviewMutation = useMutation({
    mutationFn: (firstAssessment: IPeerReviewFirstAssessment) => {
      const command: OmitUserIdProperty<ISubmitFirstAssessmentCommand> = {
        type: 'SubmitFirstAssessment',
        articleWorkflowId,
        submissionId: submissionId,
        organizationId: organizationId,
        submissionContextId: submissionContextId,
        reviewRequestId: reviewId,
        formPayload: firstAssessment,
        formType: 'reviewerFirstAssessment',
        firstName,
        lastName,
        email
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        invalidateReviewContextData();
      }
    }
  });

  return submitReviewMutation;
};

export const useSubmitEarlyAssessmentReview = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateReviewContextData
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateReviewContextData: () => void;
}) => {
  const submitReviewMutation = useMutation({
    mutationFn: (firstAssessment: IExpertFirstAssessment) => {
      const command: OmitUserIdProperty<ISubmitEarlyAssessmentReviewCommand> = {
        type: 'SubmitEarlyAssessment',
        articleWorkflowId,
        submissionId: submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        earlyAssessmentId: reviewId,
        formPayload: firstAssessment,
        formType: 'SubmitEarlyAssessment'
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        invalidateReviewContextData();
      }
    }
  });

  return submitReviewMutation;
};

export const useSubmitRevisionAssessmentReview = ({
  submissionId,
  reviewId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateReviewContextData
}: {
  submissionId: string;
  reviewId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateReviewContextData: () => void;
}) => {
  const submitRevisionReviewMutation = useMutation({
    mutationFn: (firstAssessment: IPeerReviewRevisionAssessment) => {
      const command: OmitUserIdProperty<ISubmitRevisionAssessmentCommand> = {
        type: 'SubmitRevisionAssessment',
        articleWorkflowId,
        submissionId: submissionId,
        organizationId: organizationId,
        submissionContextId: submissionContextId,
        reviewRequestId: reviewId,
        formPayload: firstAssessment,
        formType: 'reviewerRevisionAssessment'
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        invalidateReviewContextData();
      }
    }
  });

  return submitRevisionReviewMutation;
};

export const useExportSubmission = ({
  submissionId,
  submissionContextId,
  queryKey,
  organizationId,
  phaseName
}: {
  submissionContextId: string;
  submissionId: string;
  queryKey: QueryKey;
  organizationId: string;
  phaseName: IExportSubmissionCommand['targetPhase'];
}) => {
  const queryClient = useQueryClient();

  const exportSubmissionMutation = useMutation({
    mutationFn: () => {
      const command: Omit<IExportSubmissionCommand, 'userId'> = {
        type: 'ExportPreprodSubmission',
        action: 'initialized',
        targetPhase: phaseName,
        submissionId,
        submissionContextId,
        organizationId
      };

      return interactionCommandHandler<{
        response: any; // Replace with actual response type if available
      }>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        const {
          response: [exportRecord]
        } = data;

        queryClient.invalidateQueries({ queryKey: [FETCH_ACTIVITY_LOG_KEY] });

        return queryClient.setQueryData<any>(queryKey, (state: any) => ({
          ...state,
          total: state ? state.total + 1 : 0,
          items: [exportRecord!, ...(state ? state.items : [])]
        }));
      }
    }
  });

  return exportSubmissionMutation;
};

export const useCreateSubmissionNote = ({
  submissionId,
  submissionContextId,
  queryKey,
  organizationId
}: {
  submissionContextId: string;
  submissionId: string;
  queryKey: QueryKey;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();
  const createSubmissionNoteMutation = useMutation({
    mutationFn: (noteData: ICreateNotePayload) => {
      const command: OmitUserIdProperty<ISubmissionNoteCommand> = {
        ...noteData,
        type: 'SubmissionNote',
        action: 'create',
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        organizationId: organizationId
      };

      return interactionCommandHandler<{
        response: ISubmissionNote[];
      }>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        const {
          response: [note]
        } = data;
        queryClient.invalidateQueries({ queryKey: ['submission-notes'] });
        return queryClient.setQueryData<ISubmissionNotesManagementListView>(
          queryKey,
          state => ({
            ...state,
            total: state ? state.total + 1 : 0,
            items: [note!, ...(state ? state.items : [])]
          })
        );
      }
    }
  });

  return createSubmissionNoteMutation;
};

export const useUpdateSubmissionNote = ({
  submissionId,
  submissionContextId,
  queryKey,
  organizationId
}: {
  submissionContextId: string;
  submissionId: string;
  queryKey: QueryKey;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();
  const updateSubmissionNoteMutation = useMutation({
    mutationFn: (noteData: IUpdateNotePayload) => {
      const command: OmitUserIdProperty<IUpdateNoteCommand> = {
        ...noteData,
        type: 'SubmissionNote',
        action: 'update',
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        organizationId: organizationId,
        mentions: noteData?.mentions
      };

      return interactionCommandHandler<{
        response: ISubmissionNote[];
      }>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        const {
          response: [note]
        } = data;
        queryClient.setQueryData<ISubmissionNotesManagementListView>(
          queryKey,
          state => ({
            ...state,
            total: state ? state.total : 0,
            items: state
              ? state.items.map((item: ISubmissionNote) =>
                  item.id === note!.id ? note! : item
                )
              : []
          })
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['overview-details']
      });
    }
  });

  return updateSubmissionNoteMutation;
};

export const useDeleteSubmissionNote = ({
  submissionId,
  submissionContextId,
  queryKey,
  organizationId
}: {
  submissionContextId: string;
  submissionId: string;
  queryKey: QueryKey;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();
  const deleteSubmissionNoteMutation = useMutation({
    mutationFn: (noteData: IDeleteNotePayload) => {
      const command: OmitUserIdProperty<IDeleteNoteCommand> = {
        type: 'SubmissionNote',
        action: 'delete',
        id: noteData.id,
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        organizationId: organizationId,
        mentions: noteData?.mentions
      };

      return interactionCommandHandler(command);
    },
    onSuccess: (_data, noteData: IDeleteNotePayload) => {
      queryClient.setQueryData<ISubmissionNotesManagementListView>(
        queryKey,
        state => ({
          ...state,
          total: state ? state.total - 1 : 0,
          items: state
            ? state.items.filter(
                (item: ISubmissionNote) => item.id !== noteData.id
              )
            : []
        })
      );
      queryClient.invalidateQueries({
        queryKey: ['overview-details']
      });
    }
  });

  return deleteSubmissionNoteMutation;
};

export const useEditSubmissionMetadata = ({
  submissionContextId,
  submissionId,
  articleWorkflowId,
  organizationId
}: {
  submissionContextId: string;
  submissionId: string;
  articleWorkflowId: string;
  organizationId: string;
}) => {
  const editMetadataMutation = useMutation({
    mutationFn: (submission: IEditSubmission) => {
      const command: OmitUserIdProperty<IEditSubmissionCommand> = {
        type: 'EditSubmission',
        articleWorkflowId,
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        organizationId: organizationId,
        submission
      };

      return commandHandler<ICommandHandlerResponse>(command);
    }
  });

  return editMetadataMutation;
};

export const useEditSubmissionArticleType = ({
  submissionContextId,
  submissionId,
  organizationId
}: {
  submissionContextId: string;
  submissionId: string;
  organizationId: string;
}) => {
  const editMetadataMutation = useMutation({
    mutationFn: ({ type: payload }: { type: IChangeArticleType }) => {
      const command: OmitUserIdProperty<IChangeArticleTypeCommand> = {
        type: 'ChangeArticleType',
        payload,
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        organizationId: organizationId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    }
  });

  return editMetadataMutation;
};

export const useCreateSubmission = (submissionContext: {
  schemaId: string;
  submissionContextId: string;
  documentType: string;
  articleWorkflowId: string;
  articleTypeCategoryId: string;
  organizationId: string;
}) => {
  const {
    schemaId,
    submissionContextId,
    articleTypeCategoryId,
    articleWorkflowId,
    organizationId
  } = submissionContext;
  const createSubmissionMutation = useMutation({
    mutationFn: (formData: ISubmissionFormData) => {
      const {
        submittingAuthor,
        fileSet,
        preference,
        metadata,
        type,
        contactAuthor,
        correspondingAuthor,
        draftSubmissionId
      } = formData;

      const command: Omit<ICreateSubmissionCommand, 'userId'> = {
        type: 'CreateSubmission',
        submissionContextId,
        organizationId,
        schemaId,
        articleWorkflowId,
        articleTypeCategoryId,
        draftSubmissionId,
        formType: 'authorSubmission',
        formPayload: {
          type,
          metadata,
          submittingAuthor,
          contactAuthor,
          correspondingAuthor,
          fileSet,
          preference
        }
      };

      return commandHandler<ICommandHandlerResponse>(command);
    }
  });

  return createSubmissionMutation;
};

export const useManageSubmissionAssignment = ({
  submissionContextId,
  submissionId,
  subtype,
  organizationId,
  invalidateSubmissionQueries
}: {
  submissionContextId: string;
  submissionId: string;
  organizationId: string;
  subtype: ISubmissionAssignmentSubtype;
  invalidateSubmissionQueries: () => void;
}) => {
  const manageSubmissionAssignmentMutation = useMutation({
    mutationFn: (payload: ISubmissionManagementPayload) => {
      const command: OmitUserIdProperty<
        IAssignSubmissionCommand | IUnAssignSubmissionCommand
      > = {
        submissionId,
        submissionContextId,
        organizationId,
        subtype,
        ...(subtype === 'editorial' ? { role: payload.role } : {}),
        ...(payload.action === 'assign'
          ? {
              type: 'AssignSubmission',
              assigneeId: payload.assigneeId
            }
          : {
              type: 'UnassignSubmission'
            })
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return manageSubmissionAssignmentMutation;
};

export const useSubmissionAssignment = ({
  submissionContextId,
  submissionId,
  organizationId,
  invalidateSubmissionQueries
}: {
  submissionContextId: string;
  submissionId: string;
  organizationId: string;
  invalidateSubmissionQueries: () => void;
}) => {
  const queryClient = useQueryClient();
  const manageSubmissionAssignmentMutation = useMutation({
    mutationFn: ({
      assigneeId,
      role,
      type,
      replacesAssigneeId
    }: {
      assigneeId: string;
      role: string;
      type: 'AssignCOCEditor' | 'COCAssignSubmission' | 'UnassignCOCRole';
      replacesAssigneeId?: string;
    }) => {
      const command: OmitUserIdProperty<
        | IAssignCOCEditorCommand
        | ICOCAssignSubmissionCommand
        | IUnassignCOCRoleCommand
      > = {
        submissionId,
        submissionContextId,
        organizationId,
        type,
        assigneeId,
        role,
        ...(replacesAssigneeId ? { replacesAssigneeId } : {})
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['recommendation-summary', submissionId]
        });
        queryClient.invalidateQueries({
          queryKey: ['recommendation-assignees', submissionId]
        });
        queryClient.invalidateQueries({
          queryKey: ['overview-details']
        });
        queryClient.invalidateQueries({
          queryKey: ['roles-assignments', submissionContextId]
        });
        invalidateSubmissionQueries();
      }
    }
  });

  return manageSubmissionAssignmentMutation;
};

export const useModerateCOCRecommendation = ({
  submissionContextId,
  submissionId,
  organizationId,
  recommendationId
}: {
  submissionContextId: string;
  submissionId: string;
  organizationId: string;
  recommendationId: string;
}) => {
  const queryClient = useQueryClient();
  const manageSubmissionAssignmentMutation = useMutation({
    mutationFn: (formPayload: IRecommendationForm) => {
      const command: OmitUserIdProperty<IModerateCOCRecommendationCommand> = {
        submissionId,
        submissionContextId,
        organizationId,
        type: 'ModerateCOCRecommendation',
        formPayload,
        recommendationId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['recommendation-summary', submissionId]
        });

        queryClient.invalidateQueries({
          queryKey: ['overview-details']
        });
      }
    }
  });

  return manageSubmissionAssignmentMutation;
};
export const useRecommendation = ({
  submissionContextId,
  submissionId,
  organizationId,
  cocEditorId,
  role
}: {
  submissionContextId: string;
  submissionId: string;
  organizationId: string;
  cocEditorId: string;
  role: string;
}) => {
  const queryClient = useQueryClient();
  const manageSubmissionAssignmentMutation = useMutation({
    mutationFn: (formPayload: IRecommendationForm) => {
      const command: OmitUserIdProperty<ISubmitCOCRecommendationCommand> = {
        submissionId,
        submissionContextId,
        organizationId,
        cocEditorId,
        type: 'SubmitCOCRecommendation',
        formPayload,
        role
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['recommendation-summary', submissionId]
        });

        queryClient.invalidateQueries({
          queryKey: ['overview-details']
        });
      }
    }
  });

  return manageSubmissionAssignmentMutation;
};
export function createArticleType(
  contextId: string,
  data: {
    articleTypeId: string;
    fields: Record<string, FormFieldSpecification>;
  }
) {
  return axiosInstance
    .post<IFormSchema>(`submission-contexts/${contextId}/article-types`, data)
    .then(r => r.data);
}

export const useSubmitRevision = ({
  submissionId,
  submissionContextId,
  organizationId,
  articleWorkflowId,
  subtype = 'form',
  isFileUpdated,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
  articleWorkflowId: string;
  subtype?: IFormActionSubtype;
  isFileUpdated?: boolean;
  invalidateSubmissionQueries?: () => void;
}) => {
  const submitRevisionMutation = useMutation({
    mutationFn: (revisionPayload: unknown) => {
      const {
        isFileUpdated: payloadIsFileUpdated,
        isUpdated: payloadIsUpdated,
        ...formPayload
      } = (revisionPayload ?? {}) as Record<string, unknown>;
      const fileUpdated =
        payloadIsFileUpdated === undefined
          ? isFileUpdated === true
          : payloadIsFileUpdated === true;
      const command: OmitUserIdProperty<ISubmitRevisionCommand> & {
        isFileUpdated: boolean;
        isUpdated?: boolean;
      } = {
        type: 'SubmitRevision',
        subtype,
        submissionId,
        submissionContextId,
        organizationId,
        isFileUpdated: fileUpdated,
        articleWorkflowId,
        formType: 'authorRevision',
        formPayload,
        ...(typeof payloadIsUpdated === 'boolean' && {
          isUpdated: payloadIsUpdated
        })
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return submitRevisionMutation;
};

export const useAuthorCompleteChangeRequest = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  subtype,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  subtype: IFormActionSubtype;
  invalidateSubmissionQueries?: () => void;
}) => {
  const submitRequestedChangesMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: Omit<IAuthorCompleteChangeRequestCommand, 'userId'> = {
        type: 'AuthorCompleteChangeRequest',
        subtype,
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'authorChangeRequest',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return submitRequestedChangesMutation;
};

export const useRequestDeadlineExtension = ({
  submissionId,
  organizationId,
  reviewRequestId,
  submissionContextId
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  reviewRequestId: string;
}) => {
  const queryClient = useQueryClient();

  const requestDeadlineExtension = useMutation({
    mutationFn: (proposedDate: string) => {
      const command: Omit<IRequestReviewDeadlineExtensionCommand, 'userId'> = {
        type: 'RequestReviewDeadlineExtension',
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId,
        proposedDate
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['review-extension-deadline', reviewRequestId]
        });
        queryClient.invalidateQueries({
          queryKey: ['review', { reviewId: reviewRequestId }]
        });
        queryClient.invalidateQueries({
          queryKey: ['review', { id: reviewRequestId }]
        });
      }
    }
  });

  return requestDeadlineExtension;
};
export const useExtendDeadline = ({
  submissionId,
  reviewRequestId,
  organizationId,
  submissionContextId
}: {
  submissionId: string;
  submissionContextId: string;
  reviewRequestId: string;
  organizationId: string;
}) => {
  const queryClient = useQueryClient();

  const extendDeadline = useMutation({
    mutationFn: (
      payload:
        | number
        | {
            deadlineDays: number;
            source?: 'manual' | 'request';
          }
    ) => {
      const { deadlineDays, source } =
        typeof payload === 'number'
          ? { deadlineDays: payload, source: 'manual' as const }
          : payload;

      const command: Omit<IExtendReviewDeadlineCommand, 'userId'> = {
        type: 'ExtendReviewDeadline',
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId,
        deadlineDays,
        source
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['review-extension-deadline', reviewRequestId]
        });
        queryClient.invalidateQueries({
          queryKey: ['review', { id: reviewRequestId }]
        });
        queryClient.invalidateQueries({
          queryKey: ['review-list', { submissionContextId, submissionId }]
        });
        queryClient.invalidateQueries({
          queryKey: [
            'submission-context',
            submissionContextId,
            submissionId,
            'reviews'
          ]
        });
        queryClient.invalidateQueries({
          queryKey: ['summary-count', { submissionContextId, submissionId }]
        });
      }
    }
  });

  return extendDeadline;
};

export const useSubmitLicensing = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  subtype,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  subtype: IFormActionSubtype;
  invalidateSubmissionQueries?: () => void;
}) => {
  const submitLicensingMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<ISubmitLicensingCommand> = {
        type: 'SubmitLicensing',
        subtype,
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialLicensing',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return submitLicensingMutation;
};

export const useReorderReviewerQueue = ({
  submissionContextId,
  submissionId,
  reviewerInvitationId,
  queryKey
}: {
  submissionContextId: string;
  submissionId: string;
  reviewerInvitationId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();
  const reorderReviewerQueueMutation = useMutation({
    mutationFn: ({
      newPosition,
      currentPosition
    }: {
      newPosition: number;
      currentPosition: number;
    }) => {
      return axiosInstance
        .put<IReviewerInvitation>(
          `/submission-contexts/${submissionContextId}/reorder-queue`,
          {
            reviewerInvitationId,
            newPosition,
            currentPosition
          },
          {
            params: {
              submissionId
            }
          }
        )
        .then(r => ({
          ...r.data,
          isSuccess: true as const
        }))
        .catch(e => ({
          message: e.response.data,
          isSuccess: false as const
        }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return reorderReviewerQueueMutation;
};

export const useRequestRevision = (
  submissionId: string,
  submissionContextId: string,
  articleWorkflowId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload
) => {
  const requestRevisionMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IRequestRevisionCommand> = {
        type: 'RequestRevision',
        submissionContextId,
        submissionId,
        organizationId,
        articleWorkflowId,
        formType: 'editorRevisionRequest',
        formPayload,
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });
  return requestRevisionMutation;
};

export const useRequestSubmissionDeadlineExtension = ({
  submissionId,
  organizationId,
  submissionContextId
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
}) => {
  const requestSubmissionDeadlineExtension = useMutation({
    mutationFn: (proposedDate: string) => {
      const command: Omit<
        IRequestSubmissionDeadlineExtensionCommand,
        'userId'
      > = {
        type: 'RequestSubmissionDeadlineExtension',
        submissionId,
        organizationId,
        submissionContextId,
        proposedDate
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: () => {}
  });

  return requestSubmissionDeadlineExtension;
};

export const useExtendSubmissionDeadline = ({
  submissionId,
  organizationId,
  submissionContextId
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
}) => {
  const queryClient = useQueryClient();
  const { refetchSubmission } = useSubmissionData();

  const extendDeadline = useMutation({
    mutationFn: (deadlineDays: number) => {
      const command: Omit<IExtendSubmissionDeadlineCommand, 'userId'> = {
        type: 'ExtendSubmissionDeadline',
        submissionId,
        organizationId,
        submissionContextId,
        deadlineDays
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['submission-extension-deadline', submissionId]
        });
        refetchSubmission();
      }
    }
  });

  return extendDeadline;
};

export const useRemoveReviewer = ({
  submissionId,
  organizationId,
  submissionContextId,
  reviewId,
  queryKey
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  reviewId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  const removeReviewerMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IRemoveReviewCommand> = {
        type: 'RemoveReview',
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId: reviewId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        // TODO expose refetchReviewers() utility fn from SubmissionReviewsDataProvider to re-fetch all reviewer-related queries
        const reviewQueueQueryKey = [
          'submission-context',
          submissionContextId,
          submissionId,
          'reviewer-invitations'
        ];
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: reviewQueueQueryKey });
      }
    }
  });

  return removeReviewerMutation;
};

export const useReinviteReviewer = ({
  submissionId,
  organizationId,
  submissionContextId,
  queryKey
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  const reinviteReviewerMutation = useMutation({
    mutationFn: ({
      reviewerId,
      reviewId,
      emails
    }: {
      reviewerId: string;
      reviewId: string;
      emails?: IEmailCommandPayload;
    }) => {
      const command: OmitUserIdProperty<IRequestRevisionReviewCommand> = {
        type: 'RequestRevisionReview',
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId: reviewId,
        reviewerId: reviewerId,
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({
          queryKey: ['submission-workflow', submissionId]
        });
      }
    }
  });

  return reinviteReviewerMutation;
};

export const useReviewDecline = ({
  reviewId,
  queryKey,
  userId,
  token
}: {
  reviewId: string;
  queryKey: QueryKey;
  userId: string;
  token?: string;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (commandType: string) => {
      return axios
        .put<IPeerReviewWithCaseData>(
          `/api${ReviewDeclineRoute.build({ id: reviewId })}`,
          { userId, command: { type: commandType } },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
          }
        )
        .then(r => r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });
    }
  });

  return mutation;
};

export const useCoReviewDecline = ({
  reviewId,
  userId,
  queryKey
}: {
  reviewId: string;
  userId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      return axiosInstance
        .put(CoReviewDeclineRoute.build({ id: reviewId }), {
          userId
        })
        .then(r => r.data as IPeerReviewWithCaseData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });
    }
  });

  return mutation;
};

export const useCoReviewInvitationDecision = ({
  reviewId,
  decision,
  queryKey
}: {
  reviewId: string;
  decision: 'acceptRequest' | 'declineRequest';
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      return axiosInstance
        .put(CoReviewInvitationRoute.build({ id: reviewId }), {
          decision
        })
        .then(r => r.data as IPeerReviewWithCaseData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });
    }
  });

  return mutation;
};

export const useActivateReviewerQueue = (
  submissionId: string,
  submissionContextId: string,
  queryKeys: QueryKey[],
  organizationId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (active: boolean) => {
      const command: OmitUserIdProperty<IToggleQueueCommand> = {
        type: 'ToggleQueue',
        submissionId,
        organizationId,
        submissionContextId,
        active
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        for (const queryKey of queryKeys)
          queryClient.invalidateQueries({ queryKey, refetchType: 'all' });
      }
    }
  });
};

export const useOfferTransfer = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries,
  emails
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
  emails?: IEmailCommandPayload;
}) => {
  const mutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialOfferTransferCommand> = {
        type: 'EditorialOfferTransfer',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialOfferTransfer',
        formPayload,
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return mutation;
};

export const useDeclineTransfer = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const mutation = useMutation({
    mutationFn: ({ declineReason }: { declineReason: string }) => {
      const command: OmitUserIdProperty<IDeclineTransferCommand> = {
        type: 'DeclineTransfer',
        submissionContextId,
        submissionId,
        organizationId,
        declineReason
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });
  return mutation;
};

export const useAcceptTransfer = (
  submissionId: string,
  organizationId: string
) => {
  const mutation = useMutation({
    mutationFn: async ({
      submissionContextId,
      articleWorkflowId,
      subtype,
      formPayload = {},
      oldSubmissionContextId,
      oldArticleWorkflowId,
      articleTypeCategoryId,
      oldArticleTypeCategoryId
    }: {
      submissionContextId: string;
      articleWorkflowId: string;
      subtype: IFormActionSubtype;
      formPayload?: unknown;
      oldSubmissionContextId?: string;
      oldArticleWorkflowId?: string;
      articleTypeCategoryId: string;
      oldArticleTypeCategoryId?: string;
    }) => {
      const command: OmitUserIdProperty<IAcceptTransferOfferCommand> = {
        type: 'AcceptTransferOffer',
        submissionContextId,
        organizationId,
        submissionId,
        subtype,
        formType: 'authorSubmission',
        articleWorkflowId,
        formPayload,
        oldSubmissionContextId,
        oldArticleWorkflowId,
        articleTypeCategoryId,
        oldArticleTypeCategoryId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    }
  });

  return mutation;
};

export const useWithdrawSubmission = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries,
  emails,
  notifyReviewerIds
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries: () => void;
  emails?: IEmailCommandPayload;
  notifyReviewerIds?: string[];
}) => {
  const withdrawSubmissionMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialWithdrawSubmissionCommand> = {
        type: 'EditorialWithdrawSubmission',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialWithdrawSubmission',
        formPayload,
        emails,
        notifyReviewerIds
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return withdrawSubmissionMutation;
};

export const useRequestSubmissionWithdrawal = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries: () => void;
}) => {
  const withdrawSubmissionMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IAuthorRequestSubmissionWithdrawalCommand> =
        {
          type: 'AuthorRequestSubmissionWithdrawal',
          submissionId,
          organizationId,
          submissionContextId,
          articleWorkflowId,
          formType: 'authorWithdrawSubmission',
          formPayload
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return withdrawSubmissionMutation;
};

export const useRevokeSubmissionWithdrawalRequest = ({
  submissionId,
  organizationId,
  submissionContextId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  invalidateSubmissionQueries: () => void;
}) => {
  const mutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IAuthorRevokeSubmissionWithdrawalRequestCommand> =
        {
          type: 'AuthorRevokeSubmissionWithdrawalRequest',
          submissionId,
          organizationId,
          submissionContextId
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useAcceptSubmissionWithdrawalRequest = ({
  submissionId,
  organizationId,
  submissionContextId,
  invalidateSubmissionQueries,
  emails,
  notifyReviewerIds
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  invalidateSubmissionQueries: () => void;
  emails?: IEmailCommandPayload;
  notifyReviewerIds?: string[];
}) => {
  const mutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialAcceptSubmissionWithdrawalCommand> =
        {
          type: 'EditorialAcceptSubmissionWithdrawal',
          submissionId,
          organizationId,
          submissionContextId,
          emails,
          notifyReviewerIds,
          formPayload
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useSubmitChangeRequest = ({
  submissionId,
  organizationId,
  submissionContextId,
  eventSubType,
  roleContext
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  eventSubType: IFormActionSubtype;
  roleContext: IRoleType;
}) => {
  const queryKey = ['change-requests', { submissionId, roleType: roleContext }];
  const submissionFieldsQuery = ['submission-fields', submissionId];

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: IChangeRequestPayload) => {
      const command: OmitUserIdProperty<IEditorialSubmitChangeRequestCommand> =
        {
          ...payload,
          type: 'EditorialSubmitChangeRequest',
          submissionId,
          organizationId,
          submissionContextId,
          subtype: eventSubType
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(submissionFieldsQuery);
      return queryClient.invalidateQueries(queryKey);
    }
  });

  return mutation;
};

export const useUnsubmitSubmission = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries,
  emails
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries: () => void;
  emails?: IEmailCommandPayload;
}) => {
  const mutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialUnsubmitSubmissionCommand> = {
        type: 'EditorialUnsubmitSubmission',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialUnsubmit',
        formPayload,
        emails
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useResubmitSubmission = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const mutation = useMutation({
    mutationFn: (payload: { isFileUpdated: boolean; isUpdated: boolean }) => {
      const command: OmitUserIdProperty<IAuthorResubmitSubmissionCommand> & {
        isFileUpdated: boolean;
        isUpdated: boolean;
      } = {
        type: 'AuthorResubmitSubmission',
        submissionId,
        organizationId,
        submissionContextId,
        isFileUpdated: payload.isFileUpdated,
        isUpdated: payload.isUpdated
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useAuthorEditSubmission = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  subtype = 'form',
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  subtype?: IFormActionSubtype;
  invalidateSubmissionQueries?: () => void;
}) => {
  const submitRevisionMutation = useMutation({
    mutationFn: (revisionPayload: unknown) => {
      const {
        isFileUpdated: payloadIsFileUpdated,
        isUpdated: payloadIsUpdated,
        ...formPayload
      } = (revisionPayload ?? {}) as Record<string, unknown>;
      const command: OmitUserIdProperty<IAuthorEditSubmissionCommand> & {
        isFileUpdated: boolean;
        isUpdated?: boolean;
      } = {
        type: 'AuthorEditSubmission',
        subtype,
        submissionId,
        organizationId,
        submissionContextId,
        isFileUpdated: payloadIsFileUpdated === true,
        articleWorkflowId,
        formType: 'authorSubmission',
        formPayload,
        ...(typeof payloadIsUpdated === 'boolean' && {
          isUpdated: payloadIsUpdated
        })
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return submitRevisionMutation;
};

export const useRequestImmediateRevision = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload
) => {
  const provisionalAcceptanceMutation = useMutation({
    mutationFn: (editorComment: string) => {
      const command: OmitUserIdProperty<IEditorialRequestImmediateRevisionCommand> =
        {
          type: 'EditorialRequestImmediateRevision',
          submissionContextId,
          organizationId,
          submissionId,
          editorComment,
          emails
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return provisionalAcceptanceMutation;
};

export const useAcceptEarlyAssessmentRequest = ({
  submissionId,
  assessmentId,
  organizationId,
  submissionContextId,
  queryKey,
  subtype
}: {
  submissionId: string;
  assessmentId: string;
  organizationId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  subtype: 'accept' | 'decline';
}) => {
  const queryClient = useQueryClient();

  const acceptEarlyAssessmentRequestMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IEarlyAssessmentRequestDecisionCommand> =
        {
          type: 'EarlyAssessmentRequestDecision',
          subtype,
          organizationId,
          submissionId: submissionId,
          submissionContextId: submissionContextId,
          earlyAssessmentId: assessmentId
        };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ['assessment-list-total'] });
        queryClient.invalidateQueries({
          queryKey: ['early-assessment-data', { assessmentId }]
        });
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return acceptEarlyAssessmentRequestMutation;
};

export const useAdjudicationRequest = ({
  submissionId,
  adjudicationId,
  organizationId,
  submissionContextId,
  queryKey,
  subtype
}: {
  submissionId: string;
  adjudicationId: string;
  organizationId: string;
  submissionContextId: string;
  queryKey: QueryKey;
  subtype: 'accept' | 'decline';
}) => {
  const queryClient = useQueryClient();

  const acceptAdjudicationRequestMutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IAdjudicationRequestDecisionCommand> = {
        type: 'AdjudicationRequestDecision',
        subtype,
        organizationId,
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        adjudicationId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['adjudications-list-total']
        });
        queryClient.invalidateQueries({
          queryKey: ['adjudications-data', { adjudicationId }]
        });
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return acceptAdjudicationRequestMutation;
};

export const useSubmitAdjudicationReview = ({
  submissionId,
  adjudicationId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateReviewContextData
}: {
  submissionId: string;
  adjudicationId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateReviewContextData: () => void;
}) => {
  const submitReviewMutation = useMutation({
    mutationFn: (firstAssessment: IExpertFirstAssessment) => {
      const command: OmitUserIdProperty<ISubmitAdjudicationReviewCommand> = {
        type: 'SubmitAdjudication',
        articleWorkflowId,
        organizationId,
        submissionId: submissionId,
        submissionContextId: submissionContextId,
        adjudicationId,
        formPayload: firstAssessment,
        formType: 'SubmitAdjudication'
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        invalidateReviewContextData();
      }
    }
  });

  return submitReviewMutation;
};

export const useAuthorSubmitCopyrightForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const authorSubmitCopyrightMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IAuthorSubmitCopyrightCommand> = {
        type: 'AuthorSubmitCopyright',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'authorCopyrightSubmission',
        subtype: 'form',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return authorSubmitCopyrightMutation;
};

export const useSubmittingAuthorOnlySubmitCopyrightForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const authorSubmitCopyrightMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: Omit<IAuthorUploadCopyrightCommand, 'userId'> = {
        type: 'AuthorUploadCopyright',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'authorUploadCopyright',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return authorSubmitCopyrightMutation;
};

export const useEditorialUploadCopyrightForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const editorUploadCopyrightMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: OmitUserIdProperty<IEditorialUploadCopyrightCommand> = {
        type: 'EditorialUploadCopyright',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialUploadCopyright',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return editorUploadCopyrightMutation;
};

export const useEditorialRequestCopyrightForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const editorRequestCopyrightMutation = useMutation({
    mutationFn: (payload: {
      responseToAuthor: string;
      attachments: ISubmissionFile[];
    }) => {
      const command: OmitUserIdProperty<IEditorialRequestCopyrightCommand> = {
        ...payload,
        organizationId,
        type: 'EditorialRequestCopyright',
        submissionId,
        submissionContextId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return editorRequestCopyrightMutation;
};

export const useSubmittingAuthorOnlySubmitICMJEForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const authorSubmitICMJEMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: Omit<IAuthorUploadICMJECommand, 'userId'> = {
        type: 'AuthorUploadICMJE',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'authorUploadICMJE',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return authorSubmitICMJEMutation;
};

export const useEditorialUploadICMJEForm = ({
  submissionId,
  organizationId,
  submissionContextId,
  articleWorkflowId,
  invalidateSubmissionQueries
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  invalidateSubmissionQueries?: () => void;
}) => {
  const authorSubmitICMJEMutation = useMutation({
    mutationFn: (formPayload: unknown) => {
      const command: Omit<IEditorialUploadICMJECommand, 'userId'> = {
        type: 'EditorialUploadICMJE',
        submissionId,
        organizationId,
        submissionContextId,
        articleWorkflowId,
        formType: 'editorialUploadICMJE',
        formPayload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: invalidateSubmissionQueries
      ? getSubmissionSuccessCallback(invalidateSubmissionQueries)
      : undefined
  });

  return authorSubmitICMJEMutation;
};

export const useSendAdhocEmail = (
  organizationId: string,
  queryKey: QueryKey,
  submissionId?: string,
  submissionContextId?: string
) => {
  const queryClient = useQueryClient();

  const sendAdhocEmailMutation = useMutation({
    mutationFn: (payload: { email: ISendAdhocEmailCommand['email'] }) => {
      const { email } = payload;

      const command: OmitUserIdProperty<ISendAdhocEmailCommand> = {
        type: 'SendAdhocEmail',
        organizationId,
        submissionContextId,
        submissionId,
        email
      };

      return organizationInteractionHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: [FETCH_ACTIVITY_LOG_KEY] });
        queryClient.invalidateQueries({
          queryKey: ['submission-context', submissionContextId, submissionId]
        });
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return sendAdhocEmailMutation;
};

export const useDenySubmissionDeadlineExtension = ({
  submissionId,
  organizationId,
  submissionContextId
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
}) => {
  const { refetchSubmission } = useSubmissionData();

  const command = useMutation({
    mutationFn: () => {
      const command: Omit<IDenySubmissionDeadlineExtensionCommand, 'userId'> = {
        type: 'DenySubmissionDeadlineExtension',
        submissionId,
        organizationId,
        submissionContextId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        refetchSubmission();
      }
    }
  });

  return command;
};

export const useDenyReviewDeadlineExtension = ({
  submissionId,
  organizationId,
  submissionContextId,
  reviewRequestId
}: {
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  reviewRequestId: string;
}) => {
  const queryClient = useQueryClient();

  const command = useMutation({
    mutationFn: () => {
      const command: Omit<IDenyReviewDeadlineExtensionCommand, 'userId'> = {
        type: 'DenyReviewDeadlineExtension',
        reviewRequestId,
        submissionId,
        organizationId,
        submissionContextId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: ['review-extension-deadline', reviewRequestId]
        });
        queryClient.invalidateQueries({
          queryKey: ['review', { id: reviewRequestId }]
        });
        queryClient.invalidateQueries({
          queryKey: ['review-list', { submissionContextId, submissionId }]
        });
        queryClient.invalidateQueries({
          queryKey: [
            'submission-context',
            submissionContextId,
            submissionId,
            'reviews'
          ]
        });
        queryClient.invalidateQueries({
          queryKey: ['summary-count', { submissionContextId, submissionId }]
        });
      }
    }
  });

  return command;
};

export const useRevertDecision = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string,
  emails?: IEmailCommandPayload
) => {
  const mutation = useMutation({
    mutationFn: (payload: IRevertDecisionCommandData) => {
      const command: OmitUserIdProperty<IRevertDecisionCommand> = {
        type: 'RevertDecision',
        submissionContextId,
        organizationId,
        submissionId,
        emails,
        payload
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useResetAuthorCopyright = (
  submissionId: string,
  submissionContextId: string,
  invalidateSubmissionQueries: () => void,
  organizationId: string
) => {
  const mutation = useMutation({
    mutationFn: () => {
      const command: OmitUserIdProperty<IResetAuthorCopyrightCommand> = {
        type: 'ResetAuthorCopyright',
        submissionId,
        organizationId,
        submissionContextId
      };

      return commandHandler<ICommandHandlerResponse>(command);
    },
    onSuccess: getSubmissionSuccessCallback(invalidateSubmissionQueries)
  });

  return mutation;
};

export const useReviewInvitationDecline = ({
  reviewId,
  queryKey,
  userId
}: {
  reviewId: string;
  queryKey: QueryKey;
  userId: string;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      return axiosInstance
        .put(ReviewDeclineInvitationRoute.build({ id: reviewId }), {
          userId
        })
        .then(r => r.data as IPeerReviewWithCaseData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['review-list-totals'] });
    }
  });

  return mutation;
};
